import React from 'react';
import {
  Table,
  TableHeader,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@strivelabs/ui-core';
import { ProcessingStatus } from '@/hooks/useKeyValOperations';
import { formatDate } from '@/utils/date';
import type { BlogPostRowWithStatus } from './types';

export interface ContentRefreshTableProps {
  data: BlogPostRowWithStatus[];
  renderAction: (row: BlogPostRowWithStatus) => React.ReactNode;
}

// Status badge configuration with Tailwind classes
const statusBadgeConfig = {
  [ProcessingStatus.PENDING]: {
    label: 'Todo',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-700',
  },
  [ProcessingStatus.IN_PROGRESS]: {
    label: 'In Progress',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-700',
  },
  [ProcessingStatus.COMPLETED]: {
    label: 'Done',
    bgColor: 'bg-emerald-100',
    textColor: 'text-emerald-700',
  },
  [ProcessingStatus.WAITING_FOR_APPROVAL]: {
    label: 'In Review',
    bgColor: 'bg-status-review',
    textColor: 'text-custom-text-primary',
  },
  [ProcessingStatus.FAILED]: {
    label: 'Failed',
    bgColor: 'bg-red-100',
    textColor: 'text-red-700',
  },
};

// Status Badge Component using Tailwind classes
const StatusBadge: React.FC<{ status: ProcessingStatus }> = ({ status }) => {
  const config = statusBadgeConfig[status];
  return (
    <span
      className={`inline-flex items-center justify-center px-1.5 pt-px pb-0.5 rounded-badge text-xs leading-4 tracking-badge ${config.bgColor} ${config.textColor}`}
    >
      {config.label}
    </span>
  );
};

export const ContentRefreshTable: React.FC<ContentRefreshTableProps> = ({ data, renderAction }) => {
  return (
    <Table variant="default">
      <TableHeader variant="default">
        <TableRow variant="default">
          <TableHead variant="default" pinned="left" className="w-[220px]">
            Blog post URL
          </TableHead>
          <TableHead variant="default" className="w-[146px]">
            Primary keywords
          </TableHead>
          <TableHead variant="default" className="w-[102px]">
            Status
          </TableHead>
          <TableHead variant="default" className="w-[97px]">
            Date
          </TableHead>
          <TableHead variant="default" className="w-[100px]">
            Action
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.map((row) => (
          <TableRow key={row._id} variant="default">
            {/* Blog Post URL - with truncation, tooltip, and clickable link */}
            <TableCell variant="default" pinned="left" className="max-w-[220px]" title={row.data.blog_post_url}>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                <a
                  href={row.data.blog_post_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm leading-4 font-normal text-blue-600 hover:underline tracking-table-cell"
                  onClick={(e) => e.stopPropagation()}
                >
                  {row.data.blog_post_url}
                </a>
              </div>
            </TableCell>

            {/* Primary Keywords - with truncation and tooltip */}
            <TableCell variant="default" className="max-w-[146px]" title={row.data.primary_keywords}>
              <div className="overflow-hidden text-ellipsis whitespace-nowrap">
                <span className="text-sm leading-4 font-normal text-custom-text-primary tracking-table-cell">
                  {row.data.primary_keywords}
                </span>
              </div>
            </TableCell>

            {/* Status - badge with adjusted padding for vertical alignment */}
            <TableCell variant="default" className="pb-2 pt-[9px]">
              <StatusBadge status={row._status} />
            </TableCell>

            {/* Date */}
            <TableCell variant="default">
              <span className="text-sm leading-4 font-normal text-custom-text-primary whitespace-nowrap tracking-table-cell">
                {formatDate(row.created_at)}
              </span>
            </TableCell>

            {/* Action - with light purple background and special border */}
            <TableCell variant="default" className="bg-action-bg border-b border-b-custom-border">
              <div className="text-sm leading-4 font-normal text-custom-text-primary tracking-table-cell">
                {renderAction(row)}
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};
